import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth.jsx";
import { SIGN_IN_URL, INVITE_URL } from "../api.js";
import { BRAND } from "@blackthorn/shared";
import SEO from "../components/SEO.jsx";

const whyFeatures = [
  { num: "01", title: "Verification that means something", body: "Members prove the Discord account is really theirs - sign in, security check, CAPTCHA. Blocked users get told why, not just shown an error." },
  { num: "02", title: "Whitelist you control", body: "Approve or remove people from the dashboard. Every change records who did it and when." },
  { num: "03", title: "Raids get caught", body: "Set a join limit, and when a wave of new accounts hits it you can quarantine, kick, or lock the server down. It logs first and acts only when you turn it on." },
  { num: "04", title: "Alts and ban dodgers", body: "Fresh Discord accounts and repeat verifications get risk-scored, and IPs are logged as scrambled hashes to catch the same person coming back on another account. Banned accounts can't re-verify at all." },
  { num: "05", title: "Someone's watching the logs", body: "Webhook changes, channel deletions, mass bans - the stuff that happens right before a server gets nuked - trigger an alert in your channel." },
  { num: "06", title: "A record for everything", body: "Verifications, whitelist changes, config edits, mod actions - all stored and searchable later." },
];

const logEntries = [
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser7 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 3 years · role added in 0.4s" },
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser12 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 6 years · role added in 0.3s" },
  { kind: "Blocked", color: "text-bt-accentHover border-bt-accent/60 bg-bt-accent/10", text: "@discorduser815 blocked - alt score too high.", meta: "Account age: 2 days · score 88/100 · same IP hash as @discorduser7" },
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser29 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 2 years · role added in 0.5s" },
  { kind: "Whitelisted", color: "text-sky-400 border-sky-500/40 bg-sky-500/10", text: "@discorduser4 approved for whitelist.", meta: "Approved by staff in dashboard · audit-logged" },
  { kind: "Blocked", color: "text-bt-accentHover border-bt-accent/60 bg-bt-accent/10", text: "@discorduser903 blocked - on the server blocklist.", meta: "Ban check failed · tried 3 min after being banned" },
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser33 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 5 years · role added in 0.3s" },
  { kind: "Raid alert", color: "text-amber-400 border-amber-500/40 bg-amber-500/10", text: "Join-rate threshold exceeded - 19 joins in 45s.", meta: "Action: quarantine enabled · incident recorded" },
  { kind: "Blocked", color: "text-bt-accentHover border-bt-accent/60 bg-bt-accent/10", text: "@discorduser918 blocked - alt score too high.", meta: "Account age: 4 days · score 79/100 · new Discord account" },
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser51 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 1 year · role added in 0.6s" },
  { kind: "Verified", color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", text: "@discorduser58 passed verification.", meta: "Discord sign-in + CAPTCHA · account age 4 years · role added in 0.4s" },
  { kind: "Blocked", color: "text-bt-accentHover border-bt-accent/60 bg-bt-accent/10", text: "@discorduser921 blocked - alt score too high.", meta: "Account age: 1 day · score 91/100 · same IP hash as @discorduser51" },
];

const logTimes = ["13:58", "14:03", "14:05", "14:11", "14:26", "14:39", "14:44", "15:02", "15:02", "15:19", "15:36", "15:41"];

/**
 * Real screenshot of verification logs, shown in the "See it in action" section.
 * Drop your screenshot at: apps/dashboard/public/blackthorn-verification-logs.png
 * Until then, the hand-built mock preview is shown instead.
 */
function LogPreview() {
  const screenshot = "/blackthorn-verification-logs.png";
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => {
    const img = new Image();
    img.src = screenshot;
    img.onload = () => setImgOk(true);
    img.onerror = () => setImgOk(false);
  }, []);
  if (imgOk) {
    return (
      <div className="rounded-xl border border-bt-border bg-bt-surface p-2 shadow-2xl shadow-black/40">
        <img src={screenshot} alt="Screenshot of Blackthorn verification logs posted in a Discord channel" className="rounded-lg w-full" loading="lazy" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-bt-border bg-bt-surface p-5 text-left shadow-2xl shadow-black/40">
      <p className="text-xs uppercase tracking-widest text-bt-muted mb-4"># verification-logs</p>
      <div className="space-y-1">
        {logEntries.map((e, i) => (
          <div key={i} className="group rounded-lg hover:bg-bt-surfaceAlt px-3 py-2.5 -mx-3 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold text-white" style={{ background: e.kind === "Blocked" ? "#8f1d2c" : e.kind === "Raid alert" ? "#b45309" : e.kind === "Whitelisted" ? "#0369a1" : "#059669" }}>
                {e.text.replace("@", "").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium">Blackthorn</span>
                  <span className="text-[10px] uppercase tracking-wide bg-bt-bg border border-bt-border rounded px-1.5 py-0.5 text-bt-muted">APP</span>
                  <span className="text-[11px] text-bt-muted">Today at {logTimes[i]}</span>
                </div>
                <p className="text-sm mt-0.5">
                  <span className={`inline-block rounded border px-2 py-0.5 text-xs font-medium mr-2 ${e.color}`}>{e.kind}</span>
                  {e.text}
                </p>
                <p className="text-xs text-bt-muted mt-1">{e.meta}</p>
              </div>
            </div>
          </div>
        ))}
        <p className="text-[11px] text-bt-muted/60 text-center pt-2">simulated feed - your server's logs will look like this</p>
      </div>
    </div>
  );
}

function MarketingHeader() {
  const { me } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = (
    <>
      <a href="#why" className="hover:text-bt-text" onClick={() => setMenuOpen(false)}>Why Blackthorn</a>
      <a href="#action" className="hover:text-bt-text" onClick={() => setMenuOpen(false)}>See it in action</a>
      <a href="#commands" className="hover:text-bt-text" onClick={() => setMenuOpen(false)}>Commands</a>
      <Link to="/docs" className="hover:text-bt-text" onClick={() => setMenuOpen(false)}>Docs</Link>
    </>
  );
  return (
    <header className="sticky top-0 z-40 border-b border-bt-border bg-bt-bg/90 backdrop-blur">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-3">
            <img src="/blackthorn-icon.svg" alt="Blackthorn icon" className="w-8 h-8" />
            <span className="font-semibold tracking-wide">{BRAND.name}</span>
          </Link>
          <nav className="hidden md:flex gap-5 text-sm text-bt-muted">{links}</nav>
        </div>
        <div className="hidden md:flex items-center gap-3 text-sm">
          {me ? (
            <Link to="/dashboard" className="border border-bt-border rounded px-3 py-1.5 hover:border-bt-steel">Dashboard</Link>
          ) : (
            <a href={SIGN_IN_URL} className="text-bt-muted hover:text-bt-text px-3 py-1.5">Sign in</a>
          )}
          <a href={INVITE_URL} className="bg-bt-accent hover:bg-bt-accentHover text-white rounded px-4 py-1.5">Add to Discord</a>
        </div>
        {/* Mobile: compact CTA + hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <a href={INVITE_URL} className="bg-bt-accent hover:bg-bt-accentHover text-white rounded px-3 py-1.5 text-sm">Add</a>
          <button
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="border border-bt-border rounded p-2 hover:border-bt-steel"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
            )}
          </button>
        </div>
      </div>
      {/* Mobile dropdown menu */}
      {menuOpen && (
        <nav className="md:hidden border-t border-bt-border bg-bt-bg px-4 py-3 flex flex-col gap-3 text-sm text-bt-muted">
          {links}
          {me ? (
            <Link to="/dashboard" className="text-bt-text" onClick={() => setMenuOpen(false)}>Dashboard</Link>
          ) : (
            <a href={SIGN_IN_URL} onClick={() => setMenuOpen(false)}>Sign in</a>
          )}
        </nav>
      )}
    </header>
  );
}

export default function Landing() {
  const { me, loading } = useAuth();
  return (
    <div className="min-h-screen">
      <SEO
        title="Blackthorn - Verified, not guessed."
        description="Discord verification that filters out alts and banned accounts: Discord sign-in, security check, CAPTCHA. Plus whitelists, anti-raid, anti-spam, and moderation."
        path="/"
        keywords="discord verification bot, web verification, captcha verification, alt detection, anti-raid, discord security"
      />
      <MarketingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(143,29,44,0.18), transparent 70%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 pt-24 pb-16 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-bt-muted">{BRAND.tagline}</p>
          <h1 className="mt-8 text-4xl md:text-6xl font-semibold tracking-tight leading-tight max-w-3xl mx-auto">
            Know who's actually behind every account in your server.
          </h1>
          <p className="mt-6 text-bt-muted max-w-2xl mx-auto text-lg">
            Members verify through the web - sign in with Discord, pass a quick check, done.
            Alts and banned accounts get filtered out, your whitelist stays yours, and
            raids get caught before they wreck anything.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            {loading ? (
              <span className="text-bt-muted text-sm py-3">Loading-¦</span>
            ) : me ? (
              <Link to="/dashboard" className="bg-bt-accent hover:bg-bt-accentHover text-white rounded px-8 py-3 font-medium">Open dashboard</Link>
            ) : (
              <a href={INVITE_URL} className="bg-bt-accent hover:bg-bt-accentHover text-white rounded px-8 py-3 font-medium">Add to Discord</a>
            )}
            <Link to="/docs" className="border border-bt-border rounded px-8 py-3 hover:border-bt-steel font-medium">Read the docs</Link>
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" className="max-w-6xl mx-auto px-4 py-24">
        <p className="text-xs uppercase tracking-widest text-bt-muted mb-3">Why servers switch</p>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Built for servers that got burned before.</h2>
        <p className="mt-3 text-bt-muted max-w-2xl">
          Most bots stop at "linked an account." Blackthorn keeps score after that - on the
          account, the IP (hashed), and every action staff take.
        </p>
        <div className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-10">
          {whyFeatures.map((f) => (
            <div key={f.num} className="flex gap-5">
              <span className="text-bt-accent font-semibold text-lg shrink-0 tabular-nums">{f.num}</span>
              <div>
                <h3 className="font-medium mb-1.5">{f.title}</h3>
                <p className="text-sm text-bt-muted leading-relaxed">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* See it in action */}
      <section id="action" className="max-w-6xl mx-auto px-4 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-bt-muted mb-3">See it in action</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Watch it happen live.</h2>
            <p className="mt-4 text-bt-muted leading-relaxed">
              Pick a channel and Blackthorn posts there: who verified, who got blocked and why,
              whitelist decisions, raid alerts. No dashboard needed to keep an eye on things.
            </p>
            <p className="mt-4 text-bt-muted leading-relaxed">
              Each entry shows what matters - account age, how they verified, when it happened -
              so staff don't have to dig for context.
            </p>
          </div>
          <LogPreview />
        </div>
      </section>

      {/* Commands */}
      <section id="commands" className="max-w-6xl mx-auto px-4 py-24">
        <p className="text-xs uppercase tracking-widest text-bt-muted mb-3">Commands</p>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">One command to start.</h2>
        <div className="mt-10 overflow-x-auto rounded-xl border border-bt-border bg-bt-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-bt-muted border-b border-bt-border">
                <th className="px-6 py-3 font-medium">Command</th>
                <th className="px-6 py-3 font-medium">What it does</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bt-border">
              <tr><td className="px-6 py-3 font-mono text-bt-accentHover">/verify</td><td className="px-6 py-3 text-bt-muted">Send a verification link (Discord sign-in, checks, CAPTCHA).</td></tr>
              <tr><td className="px-6 py-3 font-mono text-bt-accentHover">/ban /kick /timeout /warn</td><td className="px-6 py-3 text-bt-muted">The moderation basics, with a case log.</td></tr>
              <tr><td className="px-6 py-3 font-mono text-bt-accentHover">/lock /purge /slowmode</td><td className="px-6 py-3 text-bt-muted">Channel controls for when things get messy.</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-bt-muted">
          Full command reference in the <Link to="/docs/commands" className="underline hover:text-bt-text">docs</Link>.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 pb-24">
        <div className="rounded-xl border border-bt-border bg-bt-surface p-12 text-center relative overflow-hidden">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(50% 80% at 50% 100%, rgba(143,29,44,0.15), transparent 70%)" }} />
          <h2 className="relative text-3xl font-semibold tracking-tight">Try it on your server.</h2>
          <p className="relative mt-3 text-bt-muted">Setup takes under an hour and the docs cover every step.</p>
          <div className="relative mt-8 flex justify-center gap-4">
            <a href={INVITE_URL} className="bg-bt-accent hover:bg-bt-accentHover text-white rounded px-8 py-3 font-medium">Add to Discord</a>
            <Link to="/docs" className="border border-bt-border rounded px-8 py-3 hover:border-bt-steel">Get started</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-bt-border py-10 text-center text-sm text-bt-muted space-y-2">
        <p>{BRAND.tagline}</p>
        <p>
          <Link to="/docs" className="underline hover:text-bt-text">Documentation</Link> ·{" "}
          <Link to="/docs/getting-started" className="underline hover:text-bt-text">Getting started</Link> ·{" "}
          <Link to="/docs/faq" className="underline hover:text-bt-text">FAQ</Link> ·{" "}
          <Link to="/terms" className="underline hover:text-bt-text">Terms of Service</Link> ·{" "}
          <Link to="/privacy" className="underline hover:text-bt-text">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}
